/**
 * The one screen in this app that ever holds a password.
 *
 * Three rules, and they are the whole component:
 *
 *  1. **It fetches on open and holds the answer in local state.** Nothing is
 *     cached, nothing is lifted into a store, nothing is written to storage.
 *     Closing the dialog is what destroys the value.
 *  2. **It closes itself.** A revealed credential left on a screen behind a
 *     locked-away laptop is the failure mode a vault is supposed to remove, so
 *     the dialog counts down and shuts. The countdown is visible, because a
 *     dialog that vanishes without warning reads as a crash.
 *  3. **It is covered, not masked.** The secret starts hidden behind a blur and
 *     is uncovered by a press, so an operator can copy a password on a shared
 *     screen without showing it. Copying does NOT require uncovering.
 *
 * The server has already recorded the read by the time this renders - see
 * `POST /vault/:id/reveal`. There is no way to open this dialog without leaving
 * that mark, which is why it is a dialog and not an inline toggle: an inline
 * eye is pressed by accident, and every accident would be an audit row.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Eye, EyeOff, Loader2, ShieldAlert } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { revealVaultEntry, type VaultEntry } from "../../../lib/vault";
import { Btn, Modal, useCopyToast } from "../../ui";

/** How long a revealed secret stays on screen. Long enough to type it into a
 *  terminal, short enough that walking away does not leave it there. */
const HOLD_SECONDS = 60;

interface Revealed {
  secret: string | null;
  notes: string | null;
}

/** One uncoverable value with a copy button. */
function SecretField({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  const t = useT();
  const copy = useCopyToast();
  const [shown, setShown] = useState(false);

  return (
    <div className="mb-3">
      <div className="crm-vault-head">
        <label className="crm-lab">{label}</label>
        <span className="flex items-center gap-1">
          <button
            type="button"
            className="crm-cb"
            onClick={() => setShown((v) => !v)}
            title={t(shown ? "vault.hide" : "vault.show")}
            aria-label={t(shown ? "vault.hide" : "vault.show")}
            aria-pressed={shown}
          >
            {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
          <button
            type="button"
            className="crm-cb"
            onClick={() => copy(value)}
            title={t("common.copy")}
            aria-label={t("common.copy")}
          >
            <Copy className="size-4" />
          </button>
        </span>
      </div>
      {/* `select-all` so one click takes the whole value: a password with a
          trailing space that a double-click clipped is a support call nobody
          traces back to the copy.

          `.crm-vault-val`, not `.crm-inp`: see crm-vault.css. Only the two
          properties that change with the cover state stay inline. */}
      <div
        className={`crm-vault-val select-all font-mono${multiline ? " is-multi" : ""}`}
        style={{
          filter: shown ? undefined : "blur(6px)",
          userSelect: shown ? "all" : "none",
        }}
        aria-hidden={!shown}
      >
        {value === "" ? t("vault.emptyValue") : value}
      </div>
    </div>
  );
}

export function RevealDialog({ entry, onClose }: { entry: VaultEntry; onClose: () => void }) {
  const t = useT();
  const copy = useCopyToast();
  const [data, setData] = useState<Revealed | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [left, setLeft] = useState(HOLD_SECONDS);
  // Held in a ref so the countdown effect does not re-subscribe (and restart)
  // every time the parent re-renders with a new closure.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    let alive = true;
    revealVaultEntry(entry.id)
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((e) => {
        if (!alive) return;
        const code = e instanceof ApiError ? (e.body as { error?: string } | null)?.error : undefined;
        setError(
          code === "vault_key_missing"
            ? "vault.keyMissingShort"
            : code === "vault_unreadable"
              ? "vault.unreadable"
              : "common.error",
        );
      });
    return () => {
      alive = false;
    };
  }, [entry.id]);

  // The countdown starts when the value arrives, not when the dialog opens: a
  // slow request would otherwise eat the time the operator has to read it.
  useEffect(() => {
    if (!data) return;
    setLeft(HOLD_SECONDS);
    const id = setInterval(() => {
      setLeft((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          closeRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [data]);

  const copyAll = useCallback(() => {
    if (!data?.secret) return;
    copy(data.secret);
  }, [copy, data]);

  return (
    <Modal
      title={entry.title}
      onClose={onClose}
      headExtra={
        data ? (
          <span className="crm-mut crm-tnum text-[12px]">{t("vault.hidesIn", { s: String(left) })}</span>
        ) : null
      }
      footer={
        <>
          <Btn onClick={onClose}>{t("common.close")}</Btn>
          {data?.secret ? (
            <Btn tone="brand" onClick={copyAll}>
              <Copy className="size-4" />
              {t("vault.copySecret")}
            </Btn>
          ) : null}
        </>
      }
    >
      {error ? (
        <div className="crm-alert">
          <ShieldAlert className="size-4 shrink-0" />
          <span>{t(error)}</span>
        </div>
      ) : !data ? (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : (
        <>
          {entry.username ? (
            <div className="mb-3">
              <div className="crm-vault-head">
                <label className="crm-lab">{t("vault.username")}</label>
                <button
                  type="button"
                  className="crm-cb"
                  onClick={() => copy(entry.username ?? "")}
                  title={t("common.copy")}
                  aria-label={t("common.copy")}
                >
                  <Copy className="size-4" />
                </button>
              </div>
              {/* Not a secret, so not covered: it is already on the row behind
                  this dialog, and blurring it would suggest otherwise. */}
              <div className="crm-vault-val select-all font-mono">{entry.username}</div>
            </div>
          ) : null}

          {data.secret !== null ? (
            <SecretField label={t("vault.secret")} value={data.secret} />
          ) : (
            <p className="crm-mut mb-3 text-[13px]">{t("vault.noSecretStored")}</p>
          )}

          {data.notes !== null ? <SecretField label={t("vault.notes")} value={data.notes} multiline /> : null}

          <p className="crm-mut text-[12px]">{t("vault.revealLogged")}</p>
        </>
      )}
    </Modal>
  );
}
